import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Plus, Edit2, Trash2, ArrowLeft, Loader2, Save, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPhoto = {
  url: '',
  caption: '',
  category: '',
  is_featured: false
};

export const AdminPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [formData, setFormData] = useState(emptyPhoto);
  const [saving, setSaving] = useState(false);

  const fetchPhotos = useCallback(async () => {
    try {
      const response = await fetch(`${API}/photos`);
      const data = await response.json();
      setPhotos(data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingPhoto 
        ? `${API}/admin/photos/${editingPhoto.photo_id}`
        : `${API}/admin/photos`;
      
      const response = await fetch(url, {
        method: editingPhoto ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save photo');

      toast.success(editingPhoto ? 'Photo updated!' : 'Photo added!');
      setIsDialogOpen(false);
      setEditingPhoto(null);
      setFormData(emptyPhoto);
      fetchPhotos();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save photo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      const response = await fetch(`${API}/admin/photos/${photoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Photo deleted!');
      fetchPhotos();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete photo');
    }
  };

  const openEditDialog = (photo) => {
    setEditingPhoto(photo);
    setFormData({
      url: photo.url,
      caption: photo.caption || '',
      category: photo.category || '',
      is_featured: photo.is_featured || false
    });
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingPhoto(null);
    setFormData(emptyPhoto);
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-photos">
      {/* Header */}
      <header className="bg-black border-b-4 border-[#FF00FF] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-[#FF00FF] transition-colors">
              <ArrowLeft size={20} />
              <span className="font-space-grotesk font-semibold">Back to Dashboard</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <Camera className="w-8 h-8 text-[#FF00FF]" />
            <h1 className="font-permanent-marker text-3xl text-black">Manage Photos</h1>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={openAddDialog}
                className="btn-graffiti-pink flex items-center gap-2"
                data-testid="add-photo-btn"
              >
                <Plus size={20} /> Add Photo
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white border-4 border-black rounded-xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bangers text-2xl">
                  {editingPhoto ? 'Edit Photo' : 'Add New Photo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="photo-form">
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Image *</label>
                  <ImageUpload
                    value={formData.url}
                    onChange={(url) => setFormData({ ...formData, url: url })}
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Caption</label>
                  <Input
                    value={formData.caption}
                    onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                    className="input-graffiti"
                    placeholder="Summer vibes at the park!"
                    data-testid="input-caption"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Category</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input-graffiti"
                    placeholder="Events"
                    data-testid="input-category"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="featured"
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                    data-testid="input-featured"
                  />
                  <label htmlFor="featured" className="font-space-grotesk font-semibold cursor-pointer">
                    Featured Photo
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 px-4 py-2 border-4 border-black rounded-lg font-bangers text-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-graffiti-pink flex items-center justify-center gap-2"
                    data-testid="save-photo-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                    Save
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Photos Grid */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-[#FF00FF] animate-spin mx-auto" />
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-20">
            <div className="card-graffiti inline-block">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="font-space-grotesk text-xl text-gray-600">No photos yet.</p>
              <p className="font-space-grotesk text-gray-500 mt-2">Add your first photo!</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <motion.div
                key={photo.photo_id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="relative group"
                data-testid={`photo-item-${index}`}
              >
                <div className="bg-white border-4 border-black rounded-lg overflow-hidden">
                  <div className="relative aspect-square">
                    <img 
                      src={photo.url} 
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full h-full object-cover"
                    />
                    {photo.is_featured && (
                      <div className="absolute top-2 left-2 bg-[#CCFF00] p-1 rounded-full border-2 border-black">
                        <Star size={16} className="text-black" />
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => openEditDialog(photo)}
                        className="p-2 bg-[#CCFF00] rounded-lg border-2 border-black hover:scale-110 transition-transform"
                        data-testid={`edit-photo-${index}`}
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => handleDelete(photo.photo_id)}
                        className="p-2 bg-red-500 text-white rounded-lg border-2 border-black hover:scale-110 transition-transform"
                        data-testid={`delete-photo-${index}`}
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                  
                  {photo.caption && (
                    <div className="p-2 border-t-2 border-black">
                      <p className="font-space-grotesk text-sm text-gray-700 truncate">{photo.caption}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPhotos;
